<template>
    <div v-show="!showDocEdit">
        <div class="proc-page">
            <div v-if="isLoading" class="loading-overlay"><p>Carregando...</p></div>
            <div v-if="msgAlerta" class="info-alert clickable" @click="msgAlerta = null">{{ msgAlerta }}</div>

            <ProcEditHeader
                :processo="objProcesso"
                :etapa="objEtapa"
                :perfil="objPerfil"
                :etapa-anterior="etapaAnterior"
                :etapa-proxima="etapaProxima"
                :resumo="resumoEtapa"
            />

            <main
                class="proc-content"
                :class="{
                    'proc-content--expanded': !!colunaExpandida,
                    [`proc-content--cols-${qtdColunasVisiveis}`]: true,
                }"
            >
                <ProcAnexosColumn
                    v-if="exibirColunaAnexos"
                    v-show="colunaVisivel('anexos')"
                    :anexos="lsAnexos"
                    :resumo="resumoEtapa"
                    :pode-anexar="podeAnexar"
                    :expanded="colunaExpandida === 'anexos'"
                    :status-anexo-texto="statusAnexoTexto"
                    :status-anexo-classe="statusAnexoClasse"
                    :pode-adicionar-anexo="podeAdicionarAnexo"
                    :formatar-data-hora="formatarDataHora"
                    :formatar-tamanho="formatarTamanho"
                    :hash-resumido="hashResumido"
                    @abrir-upload="abrirUpload"
                    @visualizar-anexo="visualizarAnexo"
                    @remover="removerItem"
                    @toggle-expand="alternarColuna('anexos')"
                />

                <ProcFormulariosColumn
                    v-if="exibirColunaFormularios"
                    v-show="colunaVisivel('formularios')"
                    :formularios="lsFormularios"
                    :resumo="resumoEtapa"
                    :pode-inserir-dados="podeInserirDados"
                    :expanded="colunaExpandida === 'formularios'"
                    :status-doc-texto="statusDocTexto"
                    :status-doc-classe="statusDocClasse"
                    @editar="editarDocumento"
                    @cancelar="removerItem"
                    @visualizar-anexo="visualizarAnexo"
                    @toggle-expand="alternarColuna('formularios')"
                />

                <ProcItemAnalisarColumn
                    v-if="exibirColunaAnalisar"
                    v-show="colunaVisivel('analises')"
                    :documentos="lsItemAnalisar"
                    :expanded="colunaExpandida === 'analises'"
                    :formatar-data-hora="formatarDataHora"
                    :status-analise-texto="statusAnaliseTexto"
                    :status-analise-classe="statusAnaliseClasse"
                    @visualizar-anexo="visualizarAnexo"
                    @analisar="abrirAnaliseDocumento"
                    @toggle-expand="alternarColuna('analises')"
                />

                <ProcDocumentosColumn
                    v-if="exibirColunaDocumentos"
                    v-show="colunaVisivel('documentos')"
                    :documentos="lsDocumentos"
                    :expanded="colunaExpandida === 'documentos'"
                    :formatar-data-hora="formatarDataHora"
                    :status-analise-texto="statusAnaliseTexto"
                    :status-analise-classe="statusAnaliseClasse"
                    @visualizar-anexo="visualizarAnexo"
                    @analisar="abrirAnaliseDocumento"
                    @toggle-expand="alternarColuna('documentos')"
                />
            </main>

            <footer class="proc-footer">
                <div class="footer-retorno">
                    <button
                        v-if="podeRetornar && objEtapa.idetapa_retorno1"
                        class="proc-btn proc-btn-fluxo warning"
                        type="button"
                        @click="retornarEtapa(objEtapa.idetapa_retorno1)"
                    >
                        {{ `<< Retornar "${objEtapa.nmetapa_retorno1}"` }}
                    </button>

                    <button
                        v-if="podeRetornar && objEtapa.idetapa_retorno2"
                        class="proc-btn proc-btn-fluxo warning"
                        type="button"
                        @click="retornarEtapa(objEtapa.idetapa_retorno2)"
                    >
                        {{ `<< Retornar "${objEtapa.nmetapa_retorno2}"` }}
                    </button>
                </div>

                <div class="footer-voltar">
                    <button
                        class="proc-btn proc-btn-voltar"
                        type="button"
                        @click="closeForm"
                    >
                        Voltar
                    </button>
                </div>

                <div class="footer-avanco">
                    <button
                        v-if="podeAvancar && objEtapa.idetapa_avanco1"
                        class="proc-btn proc-btn-fluxo primary"
                        type="button"
                        @click="avancarEtapa(objEtapa.idetapa_avanco1)"
                    >
                        {{ `>> Avançar "${objEtapa.nmetapa_avanco1}"` }}
                    </button>

                    <button
                        v-if="podeAvancar && objEtapa.idetapa_avanco2"
                        class="proc-btn proc-btn-fluxo primary"
                        type="button"
                        @click="avancarEtapa(objEtapa.idetapa_avanco2)"
                    >
                        {{ `>> Avançar "${objEtapa.nmetapa_avanco2}"` }}
                    </button>

                    <button
                        v-if="podeFinalizar"
                        class="proc-btn proc-btn-fluxo success"
                        type="button"
                        @click="avancarEtapa"
                    >
                        Finalizar Processo
                    </button>
                </div>
            </footer>
        </div>
    </div>


    <div v-if="showDocEdit">
        <DocEditPage :idEdicao="idDocEdicao" @close="showDocEdit = false" @save="lerDados" />
    </div>


    <ProcAnexoUploadModal
        ref="uploadModalRef"
        :anexo="anexoUploadAtual"
        v-model:nome="edNomeDoc"
        v-model:validade="edValidade"
        v-model:info="edInfo"
        :msg-alerta="msgAlertaForm"
        @salvar="salvarAnexo"
        @fechar="fecharUpload"
        @limpar-alerta="msgAlertaForm = null"
    />

    <ProcAnaliseDocumentoModal
        :documento="documentoAnaliseAtual"
        @salvar="salvarAnaliseDocumento"
        @fechar="fecharAnaliseDocumento"
    />

    <ConfirmModal ref="confirmModal" />
    <ModalJustificativaTexto ref="modalJustificativa" />
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Api } from '@/funcoes/apiExport';
import { globalStore } from '@/globals';
import { Validar } from '@/funcoes/validacoes';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import DocEditPage from '@/views/docs/formularios/DocEditPage.vue';
import ModalJustificativaTexto from '@/components/modals/ModalJustificativaTexto.vue';

import ProcEditHeader from './ProcEditHeader.vue';
import ProcAnexosColumn from './ProcAnexosColumn.vue';
import ProcFormulariosColumn from './ProcFormulariosColumn.vue';
import ProcDocumentosColumn from './ProcDocumentosColumn.vue';
import ProcAnexoUploadModal from './ProcAnexoUploadModal.vue';
import ProcAnaliseDocumentoModal from './ProcAnaliseDocumentoModal.vue';
import ProcItemAnalisarColumn from './ProcItemAnalisarColumn.vue';

const emit = defineEmits(['close', 'save']);
const props = defineProps({
    idEdicao: { type: Number, default: 0 },
});

const confirmModal = ref(null);
const modalJustificativa = ref(null);

const idDocEdicao = ref(0);
const isLoading = ref(false);
const msgAlerta = ref(null);
const msgAlertaForm = ref(null);

const objPerfil = ref(null);
const objProcesso = ref(null);
const objEtapa = ref(null);

const lsAnexos = ref([]);
const lsDocumentos = ref([]);
const lsFormularios = ref([]);
const lsItemAnalisar = ref([]);

const showDocEdit = ref(false);
const uploadModalRef = ref(null);
const anexoUploadAtual = ref(null);
const documentoAnaliseAtual = ref(null);
const colunaExpandida = ref(null);

const etapaAnterior = ref(null);
const etapaProxima = ref(null);

const edNomeDoc = ref('');
const edValidade = ref('');
const edInfo = ref('');

const podeConsultar = computed(() => objPerfil.value?.ckconsultar === 'S');
const podeInserirDados = computed(() => objPerfil.value?.ckinserir_dados === 'S');
const podeAnexar = computed(() => objPerfil.value?.ckanexar_doc === 'S');
const podeAvancar = computed(() => objPerfil.value?.ckavancar === 'S' && objEtapa.value?.ckfinal === 'N');
const podeFinalizar = computed(() => objPerfil.value?.ckavancar === 'S' && objEtapa.value?.ckfinal === 'S');
const podeRetornar = computed(() => objPerfil.value?.ckretornar === 'S');

const exibirColunaAnexos = computed(() => objEtapa.value?.ckpermite_anexo === 'S' || lsAnexos.value.length > 0);
const exibirColunaFormularios = computed(() => objEtapa.value?.ckpermite_doc === 'S' || lsFormularios.value.length > 0);
const exibirColunaAnalisar = computed(() => lsItemAnalisar.value.length > 0);
const exibirColunaDocumentos = computed(() => lsDocumentos.value.length > 0);

const qtdColunasVisiveis = computed(() => {
    return [exibirColunaAnexos.value, exibirColunaFormularios.value, exibirColunaAnalisar.value, exibirColunaDocumentos.value]
        .filter(Boolean)
        .length || 1;
});

const resumoEtapa = computed(() => {
    const qtdDocTotal = lsFormularios.value.length;
    const qtdDocOk = lsFormularios.value.filter((item) => !!item.idlaudo).length;
    const qtdDocPendente = qtdDocTotal - qtdDocOk;

    const qtdAnexoTotal = lsAnexos.value.length;
    const qtdAnexoOk = lsAnexos.value.filter((item) => !!item.idanexo).length;
    const qtdAnexoPendente = qtdAnexoTotal - qtdAnexoOk;

    return {
        qtd_doc_total: qtdDocTotal,
        qtd_doc_ok: qtdDocOk,
        qtd_doc_pendente: qtdDocPendente,
        qtd_anexo_total: qtdAnexoTotal,
        qtd_anexo_ok: qtdAnexoOk,
        qtd_anexo_pendente: qtdAnexoPendente,
    };
});

function closeForm() {
    emit('save');
    emit('close');
}

async function lerDados() {
    try {
        msgAlerta.value = null;
        msgAlertaForm.value = null;
        isLoading.value = true;

        const req = await Api.genericPost('/dados/proc', {
            idep: globalStore.idep,
            id: props.idEdicao,
        });

        objProcesso.value = req?.[0] || null;

        if (objProcesso.value.cdstatus > 3){
            msgAlerta.value = 'AENÇÃO: A edição deste processo não está mais autorizada';
            objProcesso.value = null;
        }

        if (!objProcesso.value) {
            msgAlerta.value = 'Processo não localizado.';
            return;
        }

        const rEtapa = await Api.genericPost('/dados/proc/etapa', {
            idreferencia: props.idEdicao,
            id: objProcesso.value.idetapa_atual,
        });

        objEtapa.value = rEtapa?.[0] || null;

        if (!objEtapa.value) {
            msgAlerta.value = 'Etapa atual não localizada.';
            return;
        }

        const reqPerfilEtapa = await Api.genericPost('/listas/proc/perfil/etapa', {
            idep: globalStore.idep,
            idreferencia: objEtapa.value.idepproc_etapa,
            id: globalStore.idperfil,
        });

        objPerfil.value = reqPerfilEtapa?.[0] || null;

        if (!podeConsultar.value) {
            msgAlerta.value = 'Seu perfil não possui permissão para consultar esta etapa.';
            return;
        }

        const rAnexos = await Api.genericPost('/dados/proc/etapa/tpdoc/anexar', {
            idreferencia: objEtapa.value.id,
            idep: globalStore.idep,
            idusuario: globalStore.idusuario
        });
        lsAnexos.value = rAnexos || [];

        const rDoc = await Api.genericPost('/dados/proc/etapa/epdoc/anexar', {
            idreferencia: objEtapa.value.id,
            idep: globalStore.idep,
            idusuario: globalStore.idusuario
        });
        lsFormularios.value = rDoc || [];
        

        const rItemAnalisar = await Api.genericPost('/dados/proc/etapa/item/conferir', {
            idep: globalStore.idep,
            idreferencia: objEtapa.value.id,
            idusuario: globalStore.idusuario,
        });
        lsItemAnalisar.value = rItemAnalisar || [];
        

        const rDocumentos = await Api.genericPost('/dados/proc/anexos', {
            idep: globalStore.idep,
            idreferencia: props.idEdicao,
            intparam1: objEtapa.value.id,
        });
        lsDocumentos.value = rDocumentos || [];
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}


function alternarColuna(nome) {
    colunaExpandida.value = colunaExpandida.value === nome ? null : nome;
}

function colunaVisivel(nome) {
    return !colunaExpandida.value || colunaExpandida.value === nome;
}

function statusDocTexto(doc) {
    if (doc.idlaudo) return 'Concluído';
    if (doc.iddoc) return 'Em edição';
    return 'Não iniciado';
}

function statusDocClasse(doc) {
    if (doc.idlaudo) return 'ok';
    if (doc.iddoc) return 'warning';
    return 'pending';
}

function statusAnexoTexto(anexo) {
    if (anexo.anexo_nmstatus) return anexo.anexo_nmstatus;
    if (anexo.nmstatus) return anexo.nmstatus;
    if (anexo.idanexo) return 'Enviado';
    return 'Pendente';
}

function statusAnexoClasse(anexo) {
    if (anexo.idanexo) return 'ok';
    return 'pending';
}

function statusAnaliseTexto(doc) {
    if (doc.removido_dh) return 'Removido';
    if (Number(doc.cdstatus) === 5) return 'Aprovado';
    if (Number(doc.cdstatus) === 7) return 'Reprovado';
    if (doc.dhavaliacao) return 'Analisado';
    return 'Aguardando Análise';
}

function statusAnaliseClasse(doc) {
    if (doc.removido_dh) return 'pending';
    if (doc.dhavaliacao && Number(doc.cdstatus) === 5) return 'ok';
    if (doc.dhavaliacao && Number(doc.cdstatus) === 7) return 'pending';
    if (doc.dhavaliacao) return 'warning';
    return 'pending';
}

function podeAdicionarAnexo(anexo) {
    if (!anexo.idanexo) return true;
    return anexo.ckpermite_multiplos === 'S';
}

async function editarDocumento(doc) {
    try {
        msgAlerta.value = null;
        isLoading.value = true;

        let iddoc = doc.iddoc;

        if (!iddoc) {
            iddoc = await iniciarDocumento(doc);
            doc.iddoc = iddoc;
        }

        abrirDocEditPage(iddoc);
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

async function iniciarDocumento(doc) {
    try {
        isLoading.value = true;
        msgAlerta.value = null;

        const payload = {
            nmrequisicao: 'SPDOC_GERAR',
            idep: globalStore.idep,
            idsim: objProcesso.value.idsim,
            idestabelecimento: objProcesso.value.idestabelecimento,
            idreferencia: doc.idepdocmodelo,
            idlogin: globalStore.idlogin,
        };

        const req = await Api.genericPost('/doc/exec_requisicao', payload);

        if (req.bolok !== 1) {
            msgAlerta.value = `ATENÇÃO: ${req.erro}`;
            return null;
        }

        const reqRegistro = await Api.genericPut('/cad/proc_item', {
            idproc: objProcesso.value.id,
            idepproc_item: doc.idepproc_item,
            idproc_etapa_inclusao: objEtapa.value.id,
            iddoc: req.id,
            idlogin: globalStore.idlogin,
        });

        if (reqRegistro.bolok !== 1) {
            msgAlerta.value = `ATENÇÃO: ${reqRegistro.erro}`;
            return null;
        }

        return req.id;
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
        return null;
    } finally {
        isLoading.value = false;
    }
}

function abrirDocEditPage(iddoc) {
    idDocEdicao.value = iddoc;
    showDocEdit.value = true;
}


function abrirUpload(anexo) {
    edNomeDoc.value = anexo.nmtpdoc || '';
    edValidade.value = '';
    edInfo.value = '';
    msgAlertaForm.value = null;
    anexoUploadAtual.value = anexo;
}

function fecharUpload() {
    anexoUploadAtual.value = null;
    edNomeDoc.value = '';
    edValidade.value = '';
    edInfo.value = '';
    msgAlertaForm.value = null;
}

function getUploader() {
    return uploadModalRef.value?.getUploader?.();
}

async function salvarAnexo() {
    const anexo = anexoUploadAtual.value;
    if (!anexo) return;

    msgAlertaForm.value = null;
    const uploader = getUploader();

    let x = '';
    if (!uploader?.hasFile()) x += '\n-Necessário selecionar um arquivo';

    let r = Validar.texto(edNomeDoc.value, '\n-Informe nome ou identificação do anexo');
    if (!r.ok) x += r.msg;

    if (anexo.ckvalidade === 'S') {
        r = Validar.data(edValidade.value);
        if (!r.ok) x += '\n-Verifique a data de validade';
    }

    if (x) {
        msgAlertaForm.value = `Verifique os dados informados: ${x}`;
        return;
    }

    try {
        isLoading.value = true;

        const file = uploader.getFile();
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const payloadAnexo = {
            idep: globalStore.idep,
            idsim: objProcesso.value.idsim,
            idestabelecimento: objProcesso.value.idestabelecimento,
            idanexogrupo: 1,
            idtpdoc: anexo.idtpdoc,
            idlogin: globalStore.idlogin,
            dtvalidade: edValidade.value,
            // nmdoc: edNomeDoc.value,
            nmreferencia: edNomeDoc.value, // 06-08-2026: PASSEI USAR REFERÊNCIA PARA MELHOR VISUALIZAÇÃO DOS DADOS
            obspostagem: edInfo.value,
            tipo: file.type === 'application/pdf' ? '0' : '1',
            fname: file.name,
            file_ext: ext,
            mime_type: file.type || 'application/octet-stream',
        };

        const retUpload = await uploader.upload(payloadAnexo);

        await Api.genericPut('/cad/proc_item', {
            idproc: objProcesso.value.id,
            idepproc_item: anexoUploadAtual.value.idepproc_item,
            idproc_etapa_inclusao: objEtapa.value.id,
            idanexo: retUpload.id,
            idlogin: globalStore.idlogin,
        });

        fecharUpload();
        await lerDados();
    } catch (error) {
        msgAlertaForm.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

async function visualizarAnexo(idAnexo) {
    try {
        msgAlerta.value = null;
        isLoading.value = true;

        const r = await Api.genericPost('/operacoes/solicitar/download', { id: idAnexo });

        if (r.bolok === 1) {
            window.open(r.download_url, '_blank');
        }
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

async function removerItem(idItem) {
    const rModal = await modalJustificativa.value.show();

    if (!rModal.confirmado) return;

    try {
        isLoading.value = true;

        await Api.genericPut('/cad/proc_item', {
            id: idItem,
            removido_idlogin: globalStore.idlogin,
            removido_motivo: rModal.justificativa,
            idlogin: globalStore.idlogin,
        });

        await lerDados();
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

function abrirAnaliseDocumento(doc) {
    documentoAnaliseAtual.value = doc;
}

function fecharAnaliseDocumento() {
    documentoAnaliseAtual.value = null;
}

async function salvarAnaliseDocumento(payload) {
    try {
        isLoading.value = true;
        msgAlerta.value = null;

        const ret = await Api.genericPut('/cad/proc_item_avaliacao', {
            id: 0, // VER FORMA DE ATUALIZAÇÃO
            idproc_item: payload.documento.id,
            idproc_etapa: objEtapa.value.id,
            ckok: payload.cdresultado === 'A' ? 'S' : 'N',
            justificativa: payload.justificativa,
            idlogin_avaliacao: globalStore.idlogin,
            idlogin: globalStore.idlogin,
        });
        if (ret.bolok === 0) {
            msgAlerta.value = `ATENÇÃO: ${ret.erro}`;
            return;
        }
        fecharAnaliseDocumento();
        await lerDados();
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}


function validarPendenciasEtapa() {
    const docsObrigatoriosPendentes = lsFormularios.value.filter((doc) => {
        return doc.ckobrigatorio === 'S' && !doc.idlaudo;
    });

    const anexosObrigatoriosPendentes = lsAnexos.value.filter((anexo) => {
        return anexo.ckobrigatorio === 'S' && !anexo.idanexo;
    });

    const documentosAnalisePendentes = lsItemAnalisar.value.filter((doc) => !doc.dhavaliacao && !doc.removido_dh);

    const partes = [];

    if (docsObrigatoriosPendentes.length > 0) {
        partes.push(`${docsObrigatoriosPendentes.length} formulário(s) obrigatório(s) pendente(s)`);
    }

    if (anexosObrigatoriosPendentes.length > 0) {
        partes.push(`${anexosObrigatoriosPendentes.length} anexo(s) obrigatório(s) pendente(s)`);
    }

    if (documentosAnalisePendentes.length > 0) {
        partes.push(`${documentosAnalisePendentes.length} documento(s) aguardando análise`);
    }

    if (partes.length > 0) {
        return partes.join(' e ') + '.';
    }

    return null;
}


async function avancarEtapa(idEtapa) {
    const pendencia = validarPendenciasEtapa();

    if (pendencia) {
        msgAlerta.value = pendencia;
        return;
    }

    const confirmado = await confirmModal.value.show({
        message: 'Deseja avançar este processo para a próxima etapa?',
        confirmText: 'Avançar',
        cancelText: 'Cancelar',
    });

    if (!confirmado) return;

    try {
        isLoading.value = true;

        const r = await Api.genericPost('/rotinas/executar', {
            nmrequisicao: 'processoAvancar',
            idep: globalStore.idep,
            idreferencia: objProcesso.value.id,
            idlogin: globalStore.idlogin,
            intparam1: idEtapa,
        });

        if (r.bolok === 1) {
            closeForm();
        } else {
            msgAlerta.value = `ATENÇÃO: ${r.erro}`;
        }
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

async function retornarEtapa(idEtapa) {
    const rModal = await modalJustificativa.value.show({ textLength: 5000 });
    if (!rModal.confirmado) return;

    try {
        isLoading.value = true;

        const r = await Api.genericPost('/rotinas/executar', {
            nmrequisicao: 'processoRetornar',
            idep: globalStore.idep,
            idreferencia: objProcesso.value.id,
            idlogin: globalStore.idlogin,
            intparam1: idEtapa,
            strparam1: rModal.justificativa,
        });

        if (r.bolok === 1) {
            closeForm();
        } else {
            msgAlerta.value = `ATENÇÃO: ${r.erro}`;
        }

    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

function formatarDataHora(valor) {
    if (!valor) return '-';
    return new Date(valor).toLocaleString('pt-BR');
}

function formatarTamanho(bytes) {
    if (!bytes) return '0 KB';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function hashResumido(hash) {
    if (!hash) return '-';
    if (hash.length <= 16) return hash;
    return `${hash.substring(0, 8)}...${hash.substring(hash.length - 8)}`;
}

onMounted(lerDados);
</script>


<style scoped>
.proc-page {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    gap: 8px;
}

.proc-content {
    flex: 1;
    min-height: 0;
    display: grid;
    gap: 8px;
    overflow: hidden;
}

.proc-content--cols-1 {
    grid-template-columns: 1fr;
}

.proc-content--cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.proc-content--cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.proc-content--cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
}

.proc-content--expanded {
    grid-template-columns: 1fr;
}

.proc-footer {
    flex-shrink: 0;

    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: 12px;

    min-height: 48px;
    padding: 6px 8px;

    background: #fff;
    border: 1px solid #aaa;
    border-radius: 6px;
}

.footer-retorno,
.footer-avanco {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.footer-retorno {
    justify-content: flex-start;
}

.footer-avanco {
    justify-content: flex-end;
}

.footer-voltar {
    display: flex;
    justify-content: center;
    align-items: center;
}

.proc-btn {
    width: auto !important;
    min-height: 32px;

    padding: 6px 14px !important;

    border-radius: 5px;
    font-size: 12px !important;
    line-height: 1.2;

    white-space: nowrap;
    cursor: pointer;
}

.proc-btn-fluxo {
    min-width: 180px;
    max-width: 340px;

    overflow: hidden;
    text-overflow: ellipsis;
}

.proc-btn-voltar {
    min-width: 90px;

    background: #f8fafc !important;
    color: #1f2937 !important;

    border: 1px solid #64748b !important;
    box-shadow: none !important;
}

.proc-btn-voltar:hover {
    background: #e2e8f0 !important;
}

.proc-btn.primary {
    background: #2563eb !important;
    color: #fff !important;
    border: 1px solid #1d4ed8 !important;
    box-shadow: none !important;
}

.proc-btn.primary:hover {
    background: #1d4ed8 !important;
}

.proc-btn.warning {
    background: #dc8f09 !important;
    color: #fff !important;
    border: 1px solid #493003 !important;
    box-shadow: none !important;
}

.proc-btn.warning:hover {
    background: #d97706 !important;
}

.proc-btn.success {
    background: #16a34a !important;
    color: #fff !important;
    border: 1px solid #15803d !important;
    box-shadow: none !important;
}

.proc-btn.success:hover {
    background: #15803d !important;
}

.proc-header-compact,
:deep(.proc-header-compact) {
    flex-shrink: 0;
    background: #fff;
    border: 1px solid #aaa;
    border-radius: 6px;
    padding: 7px 9px;
}

:deep(.proc-header-main) {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
}

:deep(.proc-header-info) {
    min-width: 0;
    flex: 1;
}

:deep(.proc-header-main h2) {
    margin: 0 0 2px;
    font-size: 16px;
    line-height: 1.15;
    color: #1f2937;
}

:deep(.proc-header-line) {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 10px;
    font-size: 12px;
    color: #444;
    line-height: 1.25;
}

:deep(.proc-header-line strong) {
    color: #222;
}

:deep(.proc-resumo-box) {
    display: flex;
    gap: 5px;
    flex-shrink: 0;
}

:deep(.proc-resumo-box div) {
    min-width: 64px;
    padding: 4px 6px;
    border-radius: 5px;
    background: #f1f5f9;
    text-align: center;
    border: 1px solid #d0d7de;
}

:deep(.proc-resumo-box strong) {
    display: block;
    font-size: 14px;
    line-height: 1;
}

:deep(.proc-resumo-box span) {
    display: block;
    margin-top: 2px;
    font-size: 10px;
    color: #555;
}

:deep(.proc-column) {
    min-height: 0;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #aaa;
    border-radius: 6px;
    padding: 7px;
}

:deep(.column-header) {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #fff;
    padding: 2px 0 6px;
    border-bottom: 1px solid #eee;
    margin-bottom: 7px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    text-align: left;
}

:deep(.column-header h3) {
    margin: 0;
    font-size: 15px;
    color: #1f2937;
}

:deep(.column-header span) {
    font-size: 11px;
    color: #666;
}

:deep(.proc-card) {
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 8px;
    margin-bottom: 7px;
    background: #fff;
}

:deep(.card-main) {
    display: flex;
    justify-content: space-between;
    gap: 8px;
}

:deep(.card-text) {
    min-width: 0;
    text-align: left;
}

:deep(.card-main h4) {
    margin: 0;
    font-size: 13px;
    line-height: 1.2;
    color: #1f2937;
}

:deep(.card-main p) {
    margin: 3px 0 0;
    font-size: 11px;
    color: #666;
    line-height: 1.25;
}

:deep(.status-pill) {
    white-space: nowrap;
    align-self: flex-start;
    border-radius: 999px;
    padding: 3px 7px;
    font-size: 10px;
    font-weight: 600;
    background: #eee;
}

:deep(.status-pill.ok) {
    background: #dcfce7;
    color: #14532d;
}

:deep(.status-pill.warning) {
    background: #fef3c7;
    color: #78350f;
}

:deep(.status-pill.pending) {
    background: #fee2e2;
    color: #7f1d1d;
}

:deep(.tag-row) {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
    margin-top: 6px;
}

:deep(.tag) {
    font-size: 10px;
    background: #f1f5f9;
    border-radius: 999px;
    padding: 2px 6px;
    color: #334155;
}

:deep(.tag.obrigatorio) {
    background: #ffe4e6;
    color: #881337;
}

:deep(.card-actions) {
    display: flex;
    justify-content: flex-end;
    gap: 5px;
    margin-top: 7px;
    flex-wrap: wrap;
}

:deep(.empty-box) {
    color: #777;
    background: #fafafa;
    border: 1px dashed #ddd;
    border-radius: 6px;
    padding: 14px;
    text-align: center;
    font-size: 12px;
}

:deep(.anexo-info) {
    margin-top: 8px;
    padding-top: 6px;
    border-top: 1px dashed #ddd;
    font-size: 11px;
    color: #555;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

:deep(.anexo-info strong) {
    color: #222;
}

:deep(.proc-doc-justificativa) {
    margin-top: 8px;
    padding: 8px;
    border-left: 4px solid #cbd5e1;
    background: #f8fafc;
    font-size: 12px;
    color: #334155;
    white-space: pre-line;
    text-align: left;
}

:deep(.proc-modal-upload),
:deep(.proc-modal-analise) {
    width: 720px;
    max-width: 95%;
}

:deep(.proc-modal-subtitle) {
    margin-top: -6px;
    color: #64748b;
    font-size: 13px;
}

.proc-upload-row {
    align-items: end;
}



@media (max-width: 1100px) {
    .proc-content--cols-4 {
        grid-template-columns: 1fr 1fr;
        overflow-y: auto;
    }
}

@media (max-width: 900px) {
    :deep(.proc-header-main) {
        align-items: flex-start;
        flex-direction: column;
    }

    :deep(.proc-resumo-box) {
        width: 100%;
    }

    :deep(.proc-resumo-box div) {
        flex: 1;
    }

    .proc-content,
    .proc-content--cols-2,
    .proc-content--cols-3,
    .proc-content--cols-4 {
        grid-template-columns: 1fr;
        overflow-y: auto;
    }

    :deep(.proc-column) {
        overflow-y: visible;
    }
}
</style>
